// src/pages/Login.js
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authAPI } from "../services/api";
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  CircularProgress,
} from "@mui/material";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [loading, setLoading] = useState(false);
  const [signupIncomplete, setSignupIncomplete] = useState(false);
  const [resumingCheckout, setResumingCheckout] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const getReadableError = (error) => {
    const errorMap = {
      "Invalid credentials": "The email or password you entered is incorrect. Please try again.",
      "Email and password are required": "Please enter both your email and password.",
      "Account is inactive": "Your account has been deactivated. Please contact support.",
      "Login failed": "Something went wrong. Please try again later.",
    };
    return errorMap[error] || error;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setWarning("");
    setSignupIncomplete(false);
    setLoading(true);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      if (window.gtag) {
        window.gtag('event', 'login', { method: 'website' });
      }
      navigate("/dashboard");
    } else if (result.code === 'SIGNUP_INCOMPLETE') {
      setSignupIncomplete(true);
    } else {
      setError(getReadableError(result.error));
      if (result.warning) {
        setWarning(result.warning);
      }
    }

    setLoading(false);
  };

  const handleResumeCheckout = async () => {
    setResumingCheckout(true);
    setError("");
    try {
      const res = await authAPI.resumeCheckout(formData.email);
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.error || "Failed to resume checkout. Please try again.");
      setResumingCheckout(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Second Clear
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
            Caregiver Dashboard
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {warning && !error.includes('Too many') && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {warning}
            </Alert>
          )}

          {signupIncomplete && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                It looks like you started signing up but didn't finish payment. Click below to complete your signup.
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={handleResumeCheckout}
                disabled={resumingCheckout}
                startIcon={resumingCheckout ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {resumingCheckout ? "Redirecting..." : "Complete Signup"}
              </Button>
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Sign In"}
            </Button>
            <Box sx={{ textAlign: "center", mb: 1 }}>
              <Link to="/forgot-password" style={{ textDecoration: "none" }}>
                <Typography variant="body2" color="text.secondary">
                  Forgot your password?
                </Typography>
              </Link>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <a href="https://get.secondclear.com" style={{ textDecoration: "none" }}>
                <Typography variant="body2" color="primary">
                  Don't have an account? Sign Up
                </Typography>
              </a>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
